(function roasterLogoMosaicRuntime(globalScope) {
  'use strict';

  const MIN_ROTATION_MS = 1700;
  const MAX_ROTATION_MS = 2400;
  const CROSSFADE_MS = 650;
  const MIN_BATCH_STAGGER_MS = 160;
  const MAX_BATCH_STAGGER_MS = 200;
  const CROSSFADE_EASING = 'cubic-bezier(0.45, 0, 0.55, 1)';
  const controllers = new WeakMap();

  function shuffle(items, random = Math.random) {
    const shuffled = [...items];
    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(random() * (index + 1));
      [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
    }
    return shuffled;
  }

  function tilesAreSeparated(firstIndex, secondIndex, columnCount = 3) {
    const firstRow = Math.floor(firstIndex / columnCount);
    const firstColumn = firstIndex % columnCount;
    const secondRow = Math.floor(secondIndex / columnCount);
    const secondColumn = secondIndex % columnCount;
    return Math.max(Math.abs(firstRow - secondRow), Math.abs(firstColumn - secondColumn)) > 1;
  }

  function createBatchPlanner(random = Math.random, tileCount = 9) {
    let nextBatchSize = 1;
    let lastTileIndexes = new Set();

    return {
      next() {
        const batchSize = nextBatchSize;
        nextBatchSize = batchSize === 1 ? 2 : 1;
        const candidates = Array.from({length: tileCount}, (_, index) => index)
          .filter((index) => !lastTileIndexes.has(index));
        let selection;
        if (batchSize === 1) {
          selection = shuffle(candidates, random).slice(0, 1);
        } else {
          const separatedPairs = [];
          for (let first = 0; first < candidates.length; first += 1) {
            for (let second = first + 1; second < candidates.length; second += 1) {
              if (tilesAreSeparated(candidates[first], candidates[second])) {
                separatedPairs.push([candidates[first], candidates[second]]);
              }
            }
          }
          selection = shuffle(separatedPairs, random)[0] || [];
        }
        lastTileIndexes = new Set(selection);
        return selection;
      },
    };
  }

  function mount(mosaic) {
    if (!mosaic || controllers.has(mosaic)) return controllers.get(mosaic);
    const section = mosaic.closest('[data-roaster-mosaic-section]');
    const source = section?.querySelector('[data-roaster-logo-source]');
    const tiles = Array.from(mosaic.querySelectorAll('[data-roaster-logo-tile]'));
    const logos = source
      ? Array.from(source.querySelectorAll('[data-logo-src]')).map((item) => ({
          src: item.dataset.logoSrc,
          name: item.dataset.logoName,
          scale: Number(item.dataset.logoScale) || 1,
        }))
      : [];
    const reduceMotion = globalScope.matchMedia('(prefers-reduced-motion: reduce)');
    const designMode = globalScope.Shopify?.designMode || section?.dataset.designMode === 'true';
    const minRotationMs = Number(mosaic.dataset.minRotationMs) || MIN_ROTATION_MS;
    const maxRotationMs = Number(mosaic.dataset.maxRotationMs) || MAX_ROTATION_MS;
    const crossfadeMs = Number(mosaic.dataset.crossfadeMs) || CROSSFADE_MS;
    const minBatchStaggerMs = Number(mosaic.dataset.minBatchStaggerMs) || MIN_BATCH_STAGGER_MS;
    const maxBatchStaggerMs = Number(mosaic.dataset.maxBatchStaggerMs) || MAX_BATCH_STAGGER_MS;
    const planner = createBatchPlanner(Math.random, tiles.length);
    let logoBag = [];
    let timer = null;
    let motionGeneration = 0;
    let destroyed = false;
    const activeAnimations = new Set();
    const incomingImages = new Set();

    function visibleLogoNames() {
      return new Set(tiles.map((tile) => tile.querySelector('img')?.dataset.logoName).filter(Boolean));
    }

    function refillLogoBag(reservedNames = new Set()) {
      const visible = visibleLogoNames();
      logoBag = shuffle(logos.filter((logo) => !visible.has(logo.name) && !reservedNames.has(logo.name)));
    }

    function randomBetween(minimum, maximum) {
      return minimum + Math.random() * Math.max(0, maximum - minimum);
    }

    function stopSchedule() {
      if (timer !== null) globalScope.clearTimeout(timer);
      timer = null;
    }

    function stop() {
      motionGeneration += 1;
      stopSchedule();
      for (const animation of activeAnimations) animation.cancel();
      activeAnimations.clear();
      for (const incoming of incomingImages) incoming.remove();
      incomingImages.clear();
      for (const tile of tiles) tile.classList.remove('is-crossfading');
    }

    function schedule() {
      stopSchedule();
      if (destroyed || designMode || reduceMotion.matches || document.hidden || logos.length <= tiles.length) return;
      timer = globalScope.setTimeout(rotate, randomBetween(minRotationMs, maxRotationMs));
    }

    async function prepareReplacement({tileIndex, logo}) {
      const tile = tiles[tileIndex];
      const outgoing = tile?.querySelector('img:not(.lnvc-roaster-logo-mosaic__image--incoming)');
      if (!tile || !outgoing || !logo) return null;

      const incoming = new Image(400, 160);
      incoming.alt = '';
      incoming.decoding = 'async';
      incoming.className = 'lnvc-roaster-logo-mosaic__image lnvc-roaster-logo-mosaic__image--incoming';
      incoming.dataset.logoName = logo.name;
      incoming.style.setProperty('--roaster-logo-optical-scale', String(logo.scale));
      incoming.src = logo.src;
      try {
        await incoming.decode();
      } catch (_error) {
        return null;
      }
      return {tile, outgoing, incoming, tileIndex};
    }

    async function crossfadeReplacement(replacement, delay, generation) {
      const {tile, outgoing, incoming} = replacement;
      if (generation !== motionGeneration || reduceMotion.matches || document.hidden) return false;

      tile.classList.add('is-crossfading');
      tile.append(incoming);
      incomingImages.add(incoming);
      const timing = {duration: crossfadeMs, delay, easing: CROSSFADE_EASING, fill: 'both'};
      const outgoingAnimation = outgoing.animate([{opacity: 1}, {opacity: 0}], timing);
      const incomingAnimation = incoming.animate([{opacity: 0}, {opacity: 1}], timing);
      activeAnimations.add(outgoingAnimation);
      activeAnimations.add(incomingAnimation);

      const results = await Promise.allSettled([outgoingAnimation.finished, incomingAnimation.finished]);
      activeAnimations.delete(outgoingAnimation);
      activeAnimations.delete(incomingAnimation);
      if (generation !== motionGeneration || results.some(({status}) => status === 'rejected')) {
        incoming.remove();
        incomingImages.delete(incoming);
        tile.classList.remove('is-crossfading');
        return false;
      }

      outgoing.remove();
      incoming.classList.remove('lnvc-roaster-logo-mosaic__image--incoming');
      outgoingAnimation.cancel();
      incomingAnimation.cancel();
      incomingImages.delete(incoming);
      tile.classList.remove('is-crossfading');
      return true;
    }

    async function rotate() {
      const generation = motionGeneration;
      const tileIndexes = planner.next();
      const reservedNames = new Set();
      const replacements = tileIndexes.map((tileIndex) => {
        if (logoBag.length === 0) refillLogoBag(reservedNames);
        let logo = logoBag.shift();
        while (logo && reservedNames.has(logo.name)) logo = logoBag.shift();
        if (!logo) {
          refillLogoBag(reservedNames);
          logo = logoBag.shift();
        }
        if (logo) reservedNames.add(logo.name);
        return {tileIndex, logo};
      }).filter(({logo}) => Boolean(logo));

      const prepared = (await Promise.all(replacements.map(prepareReplacement))).filter(Boolean);
      if (generation !== motionGeneration || prepared.length === 0) {
        if (generation === motionGeneration) schedule();
        return;
      }

      const batchStagger = prepared.length > 1
        ? randomBetween(minBatchStaggerMs, maxBatchStaggerMs)
        : 0;
      await Promise.all(prepared.map((replacement, index) =>
        crossfadeReplacement(replacement, index * batchStagger, generation)));
      if (generation === motionGeneration) schedule();
    }

    function syncMotionState() {
      if (reduceMotion.matches || document.hidden) stop();
      else schedule();
    }

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      stop();
      document.removeEventListener('visibilitychange', syncMotionState);
      reduceMotion.removeEventListener('change', syncMotionState);
      controllers.delete(mosaic);
    }

    document.addEventListener('visibilitychange', syncMotionState);
    reduceMotion.addEventListener('change', syncMotionState);
    const controller = {destroy};
    controllers.set(mosaic, controller);
    schedule();
    return controller;
  }

  function mountWithin(root = document) {
    if (root.matches?.('[data-roaster-logo-mosaic]')) mount(root);
    for (const mosaic of root.querySelectorAll?.('[data-roaster-logo-mosaic]') || []) mount(mosaic);
  }

  if (typeof document !== 'undefined') {
    mountWithin(document);
    document.addEventListener('shopify:section:load', (event) => mountWithin(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      for (const mosaic of event.target.querySelectorAll?.('[data-roaster-logo-mosaic]') || []) {
        controllers.get(mosaic)?.destroy();
      }
    });
  }

  const api = {createBatchPlanner, mount, tilesAreSeparated};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (globalScope) globalScope.LNVCRoasterLogoMosaic = api;
})(typeof window !== 'undefined' ? window : globalThis);
